// Cart backup and restoration utilities

const CART_BACKUP_KEY = 'cart_backup_before_payment';
const PAYMENT_IN_PROGRESS_KEY = 'payment_in_progress';

/**
 * Store cart data in localStorage before payment
 * @param {Array} cartList - Current cart items
 * @param {string} orderId - Order ID for tracking
 */
export const backupCartBeforePayment = (cartList, orderId) => {
  try {
    const backupData = {
      cartList,
      orderId,
      timestamp: Date.now(),
      paymentInProgress: true
    };
    
    localStorage.setItem(CART_BACKUP_KEY, JSON.stringify(backupData));
    localStorage.setItem(PAYMENT_IN_PROGRESS_KEY, 'true');
    
    console.log('Cart backed up before payment:', backupData);
  } catch (error) {
    console.error('Failed to backup cart:', error);
  }
};

/**
 * Get backed up cart data
 * @returns {Object|null} Backup data or null if not found
 */
export const getCartBackup = () => {
  try {
    const backupData = localStorage.getItem(CART_BACKUP_KEY);
    return backupData ? JSON.parse(backupData) : null;
  } catch (error) {
    console.error('Failed to get cart backup:', error);
    return null;
  }
};

/**
 * Clear cart backup after successful payment
 */
export const clearCartBackup = () => {
  try {
    localStorage.removeItem(CART_BACKUP_KEY);
    localStorage.removeItem(PAYMENT_IN_PROGRESS_KEY);
    console.log('Cart backup cleared after successful payment');
  } catch (error) {
    console.error('Failed to clear cart backup:', error);
  }
};

/**
 * Restore cart from backup (used on payment failure)
 * @returns {Array|null} Restored cart items or null
 */
export const restoreCartFromBackup = () => {
  try {
    const backupData = getCartBackup();
    if (backupData && backupData.cartList) {
      console.log('Restoring cart from backup:', backupData);
      return backupData.cartList;
    }
    return null;
  } catch (error) {
    console.error('Failed to restore cart from backup:', error);
    return null;
  }
};

/**
 * Check if payment is currently in progress
 * @returns {boolean}
 */
export const isPaymentInProgress = () => {
  return localStorage.getItem(PAYMENT_IN_PROGRESS_KEY) === 'true';
};

/**
 * Mark payment as completed (success or failure)
 */
export const markPaymentCompleted = () => {
  localStorage.removeItem(PAYMENT_IN_PROGRESS_KEY);
};

/**
 * Check if cart backup is expired (older than 1 hour)
 * @returns {boolean}
 */
export const isCartBackupExpired = () => {
  try {
    const backupData = getCartBackup();
    if (!backupData || !backupData.timestamp) {
      return true;
    }
    
    const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
    return (Date.now() - backupData.timestamp) > oneHour;
  } catch (error) {
    console.error('Failed to check cart backup expiry:', error);
    return true;
  }
};

/**
 * Clean up expired cart backups
 */
export const cleanupExpiredBackups = () => {
  if (isCartBackupExpired()) {
    clearCartBackup();
    console.log('Expired cart backup cleaned up');
  }
};